/**
 * Ceiling types (PRP-SKETCH-FIELD-2 E18) and room stats.
 *
 * Stored per the data contract: `room.ceiling = { type, ...fields, orientation? }`
 * (feet). A flat ceiling is stored as no `ceiling` object; `ceilingFt` always holds
 * the flat height or the lowest wall height, so v1 readers and the backend's
 * floor, perimeter and height intake keep working.
 */
import { POLICY } from './policy';
import { area, edgeOf, findRoom, outN, perim } from './geom';
import type { CeilingV2, Fail, Plan, Room } from './types';

/** The wall height a ceiling sits on (its lowest wall): what `ceilingFt` holds. */
export function ceilingWallFt(c: CeilingV2): number {
  switch (c.type) {
    case 'flat': return c.heightFt;
    case 'sloped': case 'peaked': return c.shortFt;
    case 'vaulted': case 'attic': return c.wallFt;
    default: return c.edgeFt;
  }
}

/** Every height of a ceiling moved by `d` feet (its shape kept). */
function shifted(c: CeilingV2, d: number): CeilingV2 {
  switch (c.type) {
    case 'flat': return { ...c, heightFt: c.heightFt + d };
    case 'sloped': return { ...c, shortFt: c.shortFt + d, tallFt: c.tallFt + d };
    case 'peaked': return { ...c, shortFt: c.shortFt + d, tallFt: c.tallFt + d, peakFt: c.peakFt + d };
    case 'vaulted': return { ...c, wallFt: c.wallFt + d, peakFt: c.peakFt + d };
    case 'attic': return { ...c, wallFt: c.wallFt + d, flatFt: c.flatFt + d };
    default: return { ...c, edgeFt: c.edgeFt + d };
  }
}

/** Depth of the room measured away from wall `vid` (how far a peak or flat can sit from it). */
function depthFrom(r: Room, vid: number): number | null {
  const E = edgeOf(r, vid);
  if (!E) return null;
  const n = outN(r, E.a, E.b, area(r));
  const ds = r.corners.map((p) => -((p.x - E.a.x) * n.x + (p.y - E.a.y) * n.y));
  return Math.max(...ds) - Math.min(...ds);
}

const bad = (message: string): Fail<'invalid'> => ({ ok: false, err: 'invalid', message });

/**
 * E18 validation: every field a positive number, the lowest wall at least
 * `ceiling_min_ft`, tall above short (a peaked ceiling may have equal walls), a
 * peak above both walls, offsets and widths inside the room, and an orientation
 * wall that belongs to the room.
 */
export function validateCeiling(r: Room, c: CeilingV2): { ok: true } | Fail<'invalid'> {
  const spec = POLICY.ceiling_types[c.type];
  if (!spec) return bad('Pick a ceiling type');
  for (const f of spec.fields) {
    const v = (c as unknown as Record<string, unknown>)[f];
    if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return bad('Fill in every value');
  }
  if (ceilingWallFt(c) < POLICY.ceiling_min_ft) return bad(`Walls must be at least ${POLICY.ceiling_min_ft} feet`);
  let depth: number | null = null;
  if (spec.orientation) {
    const o = (c as { orientation?: { edgeStart?: number } }).orientation;
    depth = o?.edgeStart != null ? depthFrom(r, o.edgeStart) : null;
    if (depth == null) return bad('Tap the wall the ceiling runs from');
  }
  const xs = r.corners.map((p) => p.x), ys = r.corners.map((p) => p.y);
  const minSide = Math.min(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
  switch (c.type) {
    case 'sloped':
      if (c.tallFt <= c.shortFt) return bad('The tall wall must be taller than the short wall');
      break;
    case 'peaked':
      if (c.tallFt < c.shortFt) return bad('The tall wall must not be shorter than the short wall');
      if (c.peakFt <= c.tallFt) return bad('The peak must be above both walls');
      if (c.peakOffsetFt >= depth!) return bad('The peak must be inside the room');
      break;
    case 'vaulted':
      if (c.peakFt <= c.wallFt) return bad('The peak must be above the walls');
      break;
    case 'attic':
      if (c.flatFt <= c.wallFt) return bad('The flat part must be above the walls');
      if (c.flatWidthFt >= depth!) return bad('The flat part must be narrower than the room');
      break;
    case 'tray': case 'box':
      if (c.offsetFt * 2 >= minSide) return bad('The offset must leave a middle inside the room');
      break;
    default:
      break;
  }
  return { ok: true };
}

/**
 * Set a room's ceiling (room properties sheet). A flat ceiling clears the object
 * and sets `ceilingFt`; any other type is stored and `ceilingFt` becomes its
 * lowest wall height. One undo step.
 */
export function setCeilingType(plan: Plan, rid: string, c: CeilingV2): { ok: true } | Fail<'missing' | 'invalid'> {
  const r = findRoom(plan, rid);
  if (!r) return { ok: false, err: 'missing', message: 'That room is gone' };
  const v = validateCeiling(r, c);
  if (!v.ok) return v;
  if (c.type === 'flat') delete r.ceiling; else r.ceiling = JSON.parse(JSON.stringify(c)) as CeilingV2;
  r.ceilingFt = ceilingWallFt(c);
  delete r.ceilingFromDefault;
  return { ok: true };
}

/** The room's ceiling as a full object (flat when none is stored). */
export const ceilingOf = (r: Room): CeilingV2 => r.ceiling ?? { type: 'flat', heightFt: r.ceilingFt };

/** "Ceiling height" on a non-flat room moves every height of its ceiling by the same amount. */
export function shiftCeilingTo(r: Room, ft: number): void {
  if (r.ceiling) r.ceiling = shifted(r.ceiling, ft - ceilingWallFt(r.ceiling));
}

export interface RoomStats {
  /** Floor area, SF. */
  F: number;
  /** Floor perimeter, LF. */
  PF: number;
  /** Wall height (flat height or lowest wall), feet. */
  H: number;
  /** Walls and ceiling, SF: flat ceilings only. */
  W?: number;
  C?: number;
  /** Not flat: "Xactimate calculates walls and ceiling" (E18). */
  xactimateCalculates: boolean;
}

/** E18 room stats: F and PF always; W and C only for a flat ceiling. */
export function roomStats(plan: Plan, rid: string): RoomStats | null {
  const r = findRoom(plan, rid);
  if (!r) return null;
  const F = Math.abs(area(r)), PF = perim(r), H = r.ceilingFt;
  if (r.ceiling && r.ceiling.type !== 'flat') return { F, PF, H, xactimateCalculates: true };
  return { F, PF, H, W: PF * H, C: F, xactimateCalculates: false };
}
